import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  getNotes,
  createNote,
  getReferrals,
  getEnrollments,
  isAuthenticated,
  type Note,
  type CreateNoteRequest,
} from '@/lib/memberAuth';
import { PageContainer, PageHeader, PageState } from '@/components/weavein';
import {
  FileText,
  Plus,
  AlertCircle,
  ArrowLeft,
  Calendar,
  Lock,
  Eye,
  Filter,
} from 'lucide-react';

export default function MemberNotesPage() {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [notes, setNotes] = useState<Note[]>([]);
  const [filteredNotes, setFilteredNotes] = useState<Note[]>([]);
  const [referrals, setReferrals] = useState<any[]>([]);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  // Filter state
  const [filterType, setFilterType] = useState<'all' | 'referral' | 'enrollment' | 'general'>('all');

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [noteType, setNoteType] = useState('general');
  const [subject, setSubject] = useState('');
  const [noteText, setNoteText] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [linkedReferralId, setLinkedReferralId] = useState<number | undefined>();
  const [linkedEnrollmentId, setLinkedEnrollmentId] = useState<number | undefined>();

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/member/login');
      return;
    }

    loadData();

    // Check if referral_id is in query params (for quick note)
    const referralId = searchParams.get('referral_id');
    if (referralId) {
      setLinkedReferralId(parseInt(referralId, 10));
      setCreateDialogOpen(true);
    }
  }, [navigate, searchParams]);

  useEffect(() => {
    // Apply filters
    let filtered = notes;

    if (filterType === 'referral') {
      filtered = notes.filter((n) => n.referral_id !== null);
    } else if (filterType === 'enrollment') {
      filtered = notes.filter((n) => n.enrollment_id !== null);
    } else if (filterType === 'general') {
      filtered = notes.filter((n) => !n.referral_id && !n.enrollment_id);
    }

    setFilteredNotes(filtered);
  }, [notes, filterType]);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const [notesData, refData, enrollData] = await Promise.all([
        getNotes(),
        getReferrals().catch(() => []),
        getEnrollments().catch(() => []),
      ]);

      setNotes(notesData);
      setReferrals(refData);
      setEnrollments(enrollData);
    } catch (err: any) {
      setError(err.message || 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setNoteType('general');
    setSubject('');
    setNoteText('');
    setIsPrivate(false);
    setLinkedReferralId(undefined);
    setLinkedEnrollmentId(undefined);
    setCreateDialogOpen(true);
  };

  const handleCreateNote = async () => {
    if (!noteText.trim()) {
      setError('Please enter note text');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const noteData: CreateNoteRequest = {
        noteType,
        subject: subject || undefined,
        noteText,
        isPrivate,
        referralId: linkedReferralId,
        enrollmentId: linkedEnrollmentId,
      };

      await createNote(noteData);

      // Reload notes
      await loadData();

      // Reset form
      setNoteType('general');
      setSubject('');
      setNoteText('');
      setIsPrivate(false);
      setLinkedReferralId(undefined);
      setLinkedEnrollmentId(undefined);
      setCreateDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to create note');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getNoteTypeLabel = (type: string) => {
    return type.replace(/_/g, ' ').toUpperCase();
  };

  const header = (
    <PageHeader
      leading={
        <Link to="/member/dashboard">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('notes.back', 'Back')}
          </Button>
        </Link>
      }
      title={t('notes.my_notes', 'My Notes')}
      description={<>{notes.length} note{notes.length !== 1 ? 's' : ''}</>}
      actions={
        <Button onClick={handleOpenCreateDialog}>
          <Plus className="w-4 h-4 mr-2" />
          {t('notes.add_note', 'Add Note')}
        </Button>
      }
    />
  );

  if (loading) {
    return (
      <>
        {header}
        <PageContainer size="wide">
          <PageState variant="loading" />
        </PageContainer>
      </>
    );
  }

  return (
    <>
      {header}
      <PageContainer size="wide">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <div className="flex items-center space-x-2 mb-6">
          <Filter className="w-4 h-4 text-ink-500" />
          <Button
            variant={filterType === 'all' ? 'default' : 'outline'}
            onClick={() => setFilterType('all')}
            className={filterType === 'all' ? 'bg-v2accent hover:bg-v2accent/90' : ''}
            size="sm"
          >
            All Notes ({notes.length})
          </Button>
          <Button
            variant={filterType === 'referral' ? 'default' : 'outline'}
            onClick={() => setFilterType('referral')}
            className={filterType === 'referral' ? 'bg-v2accent hover:bg-v2accent/90' : ''}
            size="sm"
          >
            Referral Notes ({notes.filter((n) => n.referral_id).length})
          </Button>
          <Button
            variant={filterType === 'enrollment' ? 'default' : 'outline'}
            onClick={() => setFilterType('enrollment')}
            className={filterType === 'enrollment' ? 'bg-v2accent hover:bg-v2accent/90' : ''}
            size="sm"
          >
            Enrollment Notes ({notes.filter((n) => n.enrollment_id).length})
          </Button>
          <Button
            variant={filterType === 'general' ? 'default' : 'outline'}
            onClick={() => setFilterType('general')}
            className={filterType === 'general' ? 'bg-v2accent hover:bg-v2accent/90' : ''}
            size="sm"
          >
            General ({notes.filter((n) => !n.referral_id && !n.enrollment_id).length})
          </Button>
        </div>

        {filteredNotes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-ink-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-ink mb-2">
                {filterType === 'all' ? 'No notes yet' : `No ${filterType} notes`}
              </h3>
              <p className="text-ink-500 mb-6">
                {filterType === 'all'
                  ? 'Add notes to track your progress, questions, or important information.'
                  : `You haven't created any ${filterType} notes yet.`}
              </p>
              <Button
                onClick={handleOpenCreateDialog}
                className="bg-v2accent hover:bg-v2accent/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('notes.add_your_first_note', 'Add Your First Note')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredNotes.map((note) => (
              <Card key={note.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {note.subject && (
                          <CardTitle className="text-xl">{note.subject}</CardTitle>
                        )}
                        {note.is_private && (
                          <div className="flex items-center text-xs text-ink-500">
                            <Lock className="w-3 h-3 mr-1" />
                            {t('notes.private', 'Private')}
                          </div>
                        )}
                        {!note.is_private && (
                          <div className="flex items-center text-xs text-ink-500">
                            <Eye className="w-3 h-3 mr-1" />
                            {t('notes.shared', 'Shared')}
                          </div>
                        )}
                      </div>
                      <CardDescription>
                        {getNoteTypeLabel(note.note_type)}
                        {note.referral_id && (
                          <span className="ml-2">· Referral #{note.referral_id}</span>
                        )}
                        {note.enrollment_id && (
                          <span className="ml-2">· Enrollment #{note.enrollment_id}</span>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="prose prose-sm max-w-none">
                    <p className="text-ink-700 whitespace-pre-wrap">{note.note_text}</p>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t text-sm text-ink-500">
                    <div className="flex items-center">
                      <Calendar className="w-3 h-3 mr-1" />
                      Created {formatDate(note.created_at)}
                    </div>
                    {note.updated_at !== note.created_at && (
                      <div className="text-xs text-ink-500">
                        Updated {formatDate(note.updated_at)}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </PageContainer>

      {/* Create Note Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('notes.add_note_1', 'Add Note')}</DialogTitle>
            <DialogDescription>
              {t('notes.add_a_note_to_track', 'Add a note to track your progress, questions, or important information.')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Note Type */}
            <div>
              <Label htmlFor="noteType">{t('notes.note_type', 'Note Type')}</Label>
              <Select value={noteType} onValueChange={setNoteType} disabled={creating}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder={t('notes.select_note_type', 'Select note type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">{t('notes.general', 'General')}</SelectItem>
                  <SelectItem value="appointment">{t('notes.appointment', 'Appointment')}</SelectItem>
                  <SelectItem value="question">{t('notes.question', 'Question')}</SelectItem>
                  <SelectItem value="progress_update">{t('notes.progress_update', 'Progress Update')}</SelectItem>
                  <SelectItem value="concern">{t('notes.concern', 'Concern')}</SelectItem>
                  <SelectItem value="reminder">{t('notes.reminder', 'Reminder')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Subject */}
            <div>
              <Label htmlFor="subject">{t('notes.subject_optional', 'Subject (Optional)')}</Label>
              <Input
                id="subject"
                placeholder={t('notes.brief_summary_of_the_note', 'Brief summary of the note')}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={creating}
                className="mt-2"
              />
            </div>

            {/* Note Text */}
            <div>
              <Label htmlFor="noteText">{t('notes.note', 'Note *')}</Label>
              <Textarea
                id="noteText"
                placeholder={t('notes.write_your_note_here', 'Write your note here...')}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                disabled={creating}
                rows={6}
                className="mt-2"
              />
            </div>

            {/* Link to Referral */}
            {referrals.length > 0 && (
              <div>
                <Label htmlFor="referral">Link to Referral (Optional)</Label>
                <Select
                  value={linkedReferralId?.toString() || 'none'}
                  onValueChange={(val) =>
                    setLinkedReferralId(val === 'none' ? undefined : parseInt(val, 10))
                  }
                  disabled={creating}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder={t('notes.select_referral', 'Select referral')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('notes.none', 'None')}</SelectItem>
                    {referrals.map((ref) => (
                      <SelectItem key={ref.id} value={ref.id.toString()}>
                        Referral #{ref.referral_number} - {ref.program_name || 'General'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Link to Enrollment */}
            {enrollments.length > 0 && (
              <div>
                <Label htmlFor="enrollment">Link to Enrollment (Optional)</Label>
                <Select
                  value={linkedEnrollmentId?.toString() || 'none'}
                  onValueChange={(val) =>
                    setLinkedEnrollmentId(val === 'none' ? undefined : parseInt(val, 10))
                  }
                  disabled={creating}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder={t('notes.select_enrollment', 'Select enrollment')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('notes.none_1', 'None')}</SelectItem>
                    {enrollments.map((enrollment) => (
                      <SelectItem key={enrollment.id} value={enrollment.id.toString()}>
                        {enrollment.program_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Privacy */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isPrivate"
                checked={isPrivate}
                onCheckedChange={(checked) => setIsPrivate(checked as boolean)}
                disabled={creating}
              />
              <Label htmlFor="isPrivate" className="text-sm cursor-pointer">
                Keep this note private (only visible to you)
              </Label>
            </div>
            {!isPrivate && (
              <p className="text-xs text-ink-500 ml-6">
                {t('notes.note_if_not_marked_private', 'Note: If not marked private, this note may be visible to your case managers.')}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              disabled={creating}
            >
              {t('notes.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleCreateNote}
              disabled={creating || !noteText.trim()}
              className="bg-v2accent hover:bg-v2accent/90"
            >
              {creating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  {t('notes.saving', 'Saving...')}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('notes.add_note_2', 'Add Note')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
